import JSZip from 'jszip';
import EXIF from 'exif-js';
import { FolderStructure, ImageData } from '../types';

// EXIF verilerini okumak için yardımcı fonksiyon (timeout ile)
export const getExifData = (arrayBuffer: ArrayBuffer): Promise<{ altitude?: number; datetime?: string }> => {
  return new Promise((resolve) => {
    // Timeout: 3 saniye
    const timeoutId = setTimeout(() => {
      console.warn('[EXIF] Timeout - EXIF okuma 3 saniyeden uzun sürdü, atlaniyor');
      resolve({});
    }, 3000);

    try {
      // EXIF kütüphanesi ArrayBuffer ile çalışır
      EXIF.getData(arrayBuffer as any, function (this: any) {
        clearTimeout(timeoutId);

        let altitude;
        let datetime;

        // GPS Altitude
        const gpsAlt = EXIF.getTag(this, 'GPSAltitude');
        const gpsAltRef = EXIF.getTag(this, 'GPSAltitudeRef'); // 0 = Above Sea Level, 1 = Below Sea Level

        if (gpsAlt !== undefined) {
          // gpsAlt rasyonel sayı veya dizi olabilir, sayıya çevir
          const altValue = typeof gpsAlt === 'number' ? gpsAlt :
            (gpsAlt.numerator && gpsAlt.denominator ? gpsAlt.numerator / gpsAlt.denominator : Number(gpsAlt));

          altitude = altValue;
          // Deniz seviyesi referansı (negatif yükseklik için)
          if (gpsAltRef === 1) {
            altitude = -altitude;
          }
        }

        // DateTime
        const dateTimeOriginal = EXIF.getTag(this, 'DateTimeOriginal');
        if (dateTimeOriginal) {
          datetime = dateTimeOriginal;
        }

        resolve({ altitude, datetime });
      });
    } catch (e) {
      clearTimeout(timeoutId);
      console.warn('[EXIF] Okuma hatası:', e);
      resolve({});
    }
  });
};

// IndexedDB için veritabanı adı ve sürümü
const DB_NAME = 'windlar_images_db';
const DB_VERSION = 1;
const STORE_NAME = 'images';

// IndexedDB açma veya oluşturma
const openDatabase = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB açılırken hata:', event);
      reject(new Error('IndexedDB açılamadı'));
    };

    request.onsuccess = (event) => {
      const db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = request.result;
      // Eğer "images" store yoksa oluştur
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'path' });
        console.log('IndexedDB images store oluşturuldu');
      }
    };
  });
};

// Veritabanından görsel alma
export const getImageFromDB = async (path: string): Promise<Blob | null> => {
  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(path);

      request.onsuccess = () => {
        const result = request.result;
        if (result && result.blob) {
          resolve(result.blob);
        } else {
          resolve(null);
        }
      };

      request.onerror = (event) => {
        console.error('Görsel veritabanından alınamadı:', event);
        reject(new Error('Görsel veritabanından alınamadı'));
      };
    });
  } catch (error) {
    console.error('Veritabanı işlemi sırasında hata:', error);
    return null;
  }
};

// Veritabanına görsel kaydetme
export const saveImageToDB = async (path: string, blob: Blob): Promise<void> => {
  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put({ path, blob, timestamp: Date.now() });

      request.onsuccess = () => {
        // console.log(`Görsel veritabanına kaydedildi: ${path}`);
        resolve();
      };

      request.onerror = (event) => {
        console.error('Görsel veritabanına kaydedilemedi:', event);
        reject(new Error('Görsel veritabanına kaydedilemedi'));
      };
    });
  } catch (error) {
    console.error('Veritabanı kayıt işlemi sırasında hata:', error);
  }
};

// Veritabanını temizleme (yeni zip yüklendiğinde eski görselleri sil)
const clearImagesFromDB = async (): Promise<void> => {
  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('Tüm görseller veritabanından temizlendi');
        resolve();
      };

      request.onerror = (event) => {
        console.error('Veritabanı temizleme hatası:', event);
        reject(new Error('Veritabanı temizlenemedi'));
      };
    });
  } catch (error) {
    console.error('Veritabanı temizleme işlemi sırasında hata:', error);
  }
};

// Zip dosyasını açıp içeriğini analiz eden fonksiyon
export const processZipFile = async (file: File): Promise<{
  folderStructure: FolderStructure;
  images: ImageData[];
}> => {
  // Yeni zip yüklendiğinde eski görselleri temizle
  await clearImagesFromDB();

  const zip = new JSZip();
  const zipContent = await zip.loadAsync(file);

  // Türbin adını zip dosya adından çıkar (örn. "7.zip" -> "7")
  const turbineName = file.name.replace('.zip', '');

  const folderStructure: FolderStructure = {
    turbineName,
    blades: {},
  };

  const images: ImageData[] = [];

  // Zip dosyasındaki klasörleri analiz et
  const processPaths = async () => {
    const entries = Object.entries(zipContent.files);
    console.log(`Zip içinde ${entries.length} dosya bulundu`);

    // Çıkarılan görsellerin sayısını takip etmek için
    let extractedImageCount = 0;

    // Hata raporlama için değişkenler
    let lastRejectedPath = '';
    let lastRejectionReason = '';
    let processedFilesCount = 0;

    for (const [rawPath, zipEntry] of entries) {
      // Windows kaynaklı ters çizgileri düzelt (backslashes to forward slashes)
      const relativePath = rawPath.replace(/\\/g, '/');

      // __MACOSX klasörünü ve gizli dosyaları yoksay
      if (relativePath.includes('__MACOSX') || relativePath.split('/').pop()?.startsWith('.')) continue;

      if (zipEntry.dir) continue;

      processedFilesCount++;

      // Sadece jpeg/jpg/png dosyaları kabul et
      if (!/\.(jpeg|jpg|png)$/i.test(relativePath)) {
        if (!lastRejectedPath) {
          lastRejectedPath = relativePath;
          lastRejectionReason = 'Dosya uzantısı desteklenmiyor (.jpg, .png beklenen)';
        }
        continue;
      }

      // Klasör yapısını ayrıştır: {türbin}/[kanat]/[yüzey]/[dosya.jpeg]
      const pathParts = relativePath.split('/');

      // En az 3 seviye olmalı: [kanat]/[yüzey]/[dosya.jpeg]
      if (pathParts.length < 3) {
        lastRejectedPath = relativePath;
        lastRejectionReason = 'Klasör yapısı eksik (Derinlik < 3)';
        continue;
      }

      const fileName = pathParts[pathParts.length - 1];
      const surfaceType = pathParts[pathParts.length - 2]; // LE, TE, PS, SS
      const bladeName = pathParts[pathParts.length - 3]; // Kanat ismi

      // Geçerli yüzey tipi kontrolü (LE, TE, PS, SS) - Büyük/küçük harf duyarsız
      const validSurfaces = ['LE', 'TE', 'PS', 'SS'];
      if (!validSurfaces.includes(surfaceType.toUpperCase())) {
        lastRejectedPath = relativePath;
        lastRejectionReason = `Geçersiz yüzey tipi: '${surfaceType}' (Beklenen: LE, TE, PS, SS)`;
        continue;
      }

      // surfaceType'ı normalize et (büyük harf)
      const normalizedSurfaceType = surfaceType.toUpperCase();

      try {
        console.log(`[ZIP] Processing ${fileName}...`);

        // EXIF verisi okumak için ArrayBuffer al
        const arrayBuffer = await zipEntry.async('arraybuffer');
        console.log(`[ZIP] ${fileName} - ArrayBuffer size: ${arrayBuffer.byteLength} bytes`);

        // EXIF extraction with timeout (Optimized: use only first 128KB)
        const headerBuffer = arrayBuffer.slice(0, 128 * 1024); // 128KB
        const { altitude, datetime } = await getExifData(headerBuffer);
        console.log(`[EXIF] ${fileName} - Altitude: ${altitude}, DateTime: ${datetime}`);

        // Determine MIME type
        let mimeType = 'image/jpeg'; // Default
        if (relativePath.toLowerCase().endsWith('.png')) {
          mimeType = 'image/png';
        }

        // 1. ORIGINAL BLOB - Save to DB for future upload
        const originalBlob = new Blob([arrayBuffer], { type: mimeType });
        await saveImageToDB(relativePath, originalBlob);
        console.log(`[ZIP] ${fileName} - Original Blob saved to DB (${originalBlob.size} bytes)`);

        // 2. RESIZE IMAGE - For fast navigation & UI
        const resizedBlob = await new Promise<Blob>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            const MAX_SIZE = 2500; // 2500px is enough for inspection

            if (width > MAX_SIZE || height > MAX_SIZE) {
              if (width > height) {
                height = Math.round((height * MAX_SIZE) / width);
                width = MAX_SIZE;
              } else {
                width = Math.round((width * MAX_SIZE) / height);
                height = MAX_SIZE;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              resolve(originalBlob); // Fallback
              return;
            }

            ctx.drawImage(img, 0, 0, width, height);

            // JPEG quality 0.85
            canvas.toBlob((blob) => {
              if (blob) resolve(blob);
              else resolve(originalBlob);
            }, 'image/jpeg', 0.85);
          };
          img.onerror = () => resolve(originalBlob); // Fallback
          img.src = URL.createObjectURL(originalBlob);
        });

        console.log(`[ZIP] ${fileName} - Resized Preview created (${resizedBlob.size} bytes)`);

        // 3. PREVIEW BLOB - Save to DB (key: path_preview)
        // Note: We deliberately use a different key so we don't overwrite the original
        await saveImageToDB(relativePath + '_preview', resizedBlob);

        // 4. Use RESIZED blob for UI URL (Fast Navigation)
        const url = URL.createObjectURL(resizedBlob);

        const imageData: ImageData = {
          path: relativePath,
          url,
          turbineName,
          bladeName,
          surfaceType: normalizedSurfaceType,
          fileName,
          altitude,
          datetime,
        };

        images.push(imageData);
        console.log(`[ZIP] ${fileName} - Added to images array (total: ${images.length})`);

        extractedImageCount++;

        // Klasör yapısına ekle
        if (!folderStructure.blades[bladeName]) {
          folderStructure.blades[bladeName] = {
            surfaces: {},
          };
        }

        if (!folderStructure.blades[bladeName].surfaces[normalizedSurfaceType]) {
          folderStructure.blades[bladeName].surfaces[normalizedSurfaceType] = [];
        }

        folderStructure.blades[bladeName].surfaces[normalizedSurfaceType].push(fileName);
      } catch (error: any) {
        lastRejectionReason = `${fileName} işlenirken hata: ${error.message || error}`;
        console.error(`[ZIP ERROR] ${fileName} - ${error.message}`, error);
        console.error(`[ZIP ERROR] Stack:`, error.stack);
        continue;
      }
    }

    console.log(`Toplam ${extractedImageCount} görsel çıkarıldı ve işlendi`);

    // Eğer hiç görsel bulunamadıysa detaylı hata fırlat
    if (extractedImageCount === 0) {
      if (entries.length === 0) throw new Error('Zip dosyası boş.');

      throw new Error(`Zip dosyasında geçerli bir yapı bulunamadı.
       İncelenen Dosya Sayısı: ${processedFilesCount}
       Son Reddedilen Dosya: ${lastRejectedPath || 'Yok'}
       Reddedilme Sebebi: ${lastRejectionReason || 'Dosya yapısı veya uzantısı uyumsuz'}
       
       Beklenen Yapı: [Kanat Adı]/[Yüzey (LE,TE,PS,SS)]/[Resim.jpg]`);
    } else {
      console.log(`Toplam ${extractedImageCount} görsel IndexedDB'ye kaydedildi`);
    }
  };

  await processPaths();

  return { folderStructure, images };
};

// Görüntüyü veritabanından yükleme ve URL oluşturma
export const loadImageFromDB = async (path: string): Promise<string | null> => {
  try {
    const blob = await getImageFromDB(path);
    if (blob) {
      // MIME tipini doğru belirle
      let mimeType = 'image/jpeg'; // Varsayılan
      if (path.toLowerCase().endsWith('.png')) {
        mimeType = 'image/png';
      }

      const typedBlob = new Blob([blob], { type: mimeType });
      const url = URL.createObjectURL(typedBlob);
      return url;
    }
    return null;
  } catch (error) {
    console.error('Görsel yüklenirken hata:', error);
    return null;
  }
};

// Belirli bir klasör yolundaki görselleri filtreleme
export const filterImagesByPath = (
  images: ImageData[],
  bladeName: string,
  surfaceType: string
): ImageData[] => {
  return images.filter(
    (image) => image.bladeName === bladeName && image.surfaceType === surfaceType
  );
};

// Yolu temizleme (özel karakterleri ve boşlukları düzenleme)
export const normalizePath = (path: string): string => {
  return path.replace(/[^a-zA-Z0-9/.-]/g, '_');
};